import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth';
import type { SyncLogBatchDetailsResponse } from '@/types/api.types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // 允许跨域携带 httpOnly Cookie
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 是否正在刷新 token
let isRefreshing = false;
// 等待刷新的请求队列
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // 跳过登录/注册/刷新 token 的请求，避免死循环
    const url = originalRequest?.url || '';
    if (url.includes('/auth/login/') || url.includes('/auth/register/') || url.includes('/auth/refresh/')) {
      return Promise.reject(error);
    }

    // Subscription required / expired
    if (error.response?.status === 402) {
      const data = error.response.data as { message?: string; detail?: string };
      if (data.message === 'SUBSCRIPTION_REQUIRED') {
        // 不自动跳转，让调用方处理弹窗
        console.warn('Subscription required:', data.detail);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 正在刷新 token，将请求加入队列等待
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // 尝试刷新 token；refresh token 通过 httpOnly Cookie 自动携带
        const response = await authApi.refresh();
        const { access } = response.data;

        // 更新 auth store 中的 token
        useAuthStore.setState({ token: access });

        // 处理队列中的请求
        processQueue(null, access);

        // 重试原始请求
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // 刷新失败，退出登录
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// 后端错误码映射（code → 中文提示）
export const API_ERROR_CODES: Record<string, string> = {
  EFLIGHT_INVALID_CREDENTIALS: 'eFlight 用户名或密码错误，请检查凭证',
  EFLIGHT_NETWORK_ERROR: 'eFlight 服务连接失败，请稍后重试',
  FSOP_INVALID_MESSID: 'FSOP 登录令牌无效或已过期，请重新获取 messid',
  FSOP_NETWORK_ERROR: 'FSOP 服务连接失败，请稍后重试',
  SUBSCRIPTION_REQUIRED: '需要订阅才能使用该功能',
  VALIDATION_ERROR: '请求参数校验失败',
  EMAIL_NOT_VERIFIED: '邮箱尚未验证，请查收验证邮件',
  ACCOUNT_LOCKED: '账号已锁定，验证码已发送至您的邮箱',
  INVALID_VERIFICATION_CODE: '验证码错误或已过期',
  OTP_RATE_LIMITED: '验证码已发送，请等待 10 分钟后再试',
  REGISTRATION_IP_BANNED: '注册请求过于频繁，请 24 小时后再试',
  VERIFICATION_CODE_REQUIRED: '请输入发送至邮箱的验证码以完成注册',
  EMAIL_CHANGE_OTP_SENT: '验证码已发送至您当前的邮箱，请输入后重新提交',
  CREDENTIAL_RATE_LIMITED: '凭据验证失败次数过多，请 15 分钟后再试',
  WRONG_CURRENT_PASSWORD: '当前密码不正确',
  EMAIL_DAILY_LIMIT_REACHED: '今日验证邮件已达上限，请 24 小时后再试',
  EMAIL_ALREADY_REGISTERED: '该邮箱已被注册',
  CAPTCHA_REQUIRED: '请输入图形验证码',
  INVALID_CAPTCHA: '图形验证码错误，请重新输入',
};

/** 解析后端错误响应，优先使用 code 映射后中文提示 */
export const parseApiError = (error: any): { message: string; code?: string } => {
  const responseData = error?.response?.data;
  const code = responseData?.code;
  const defaultMessage = responseData?.message || error?.message || '操作失败，请稍后重试';
  return {
    message: (code && API_ERROR_CODES[code]) || defaultMessage,
    code,
  };
};

// Auth API
export const authApi = {
  login: (username: string, password: string, extra?: Record<string, string>) =>
    api.post('/auth/login/', { username, password, ...extra }),

  register: (data: {
    email: string;
    password: string;
    passwordConfirm: string;
    displayName?: string;
  }) => api.post('/auth/register/', data),

  /** 刷新 access token；refresh token 通过 httpOnly Cookie 自动携带 */
  refresh: () => api.post('/auth/refresh/', {}),

  /** 登出：后端清除 httpOnly refresh token Cookie */
  logout: () => api.post('/auth/logout/', {}),

  /** 验证邮箱 Token，成功后返回 JWT */
  verifyEmail: (token: string) =>
    api.get('/auth/verifyEmail/', { params: { token } }),

  /** 获取图形验证码 */
  getCaptcha: () =>
    api.get<{ success: boolean; data: { captchaToken: string; captchaImage: string } }>('/auth/captcha/'),

  /** 重发邮箱验证邮件（含 OTP）。传 userId 或 email。返回当日已发送次数和上限。 */
  resendVerification: (userIdOrEmail: number | string) =>
    api.post<{ success: boolean; message: string; dailySent?: number; dailyLimit?: number }>(
      '/auth/resendVerification/',
      typeof userIdOrEmail === 'number' ? { userId: userIdOrEmail } : { email: userIdOrEmail }
    ),

  /** 通过 6 位 OTP 验证邮箱或解锁账号 */
  verifyCode: (data: { purpose: 'verify_email' | 'unlock_account'; userId: number; code: string }) =>
    api.post<{ success: boolean; data?: { user: Record<string, any>; access: string } }>('/auth/verifyCode/', data),

  /** 忘记密码：发 OTP 到邮箱，返回 userId */
  forgotPassword: (email: string) =>
    api.post<{ success: boolean; userId?: number; message: string }>('/auth/forgotPassword/', { email }),

  /** 重置密码：验证 OTP 后设置新密码 */
  resetPassword: (data: { userId: number; code: string; newPassword: string }) =>
    api.post('/auth/resetPassword/', data),
};

// User API
export const userApi = {
  getProfile: () => api.get('/user/profile/'),
  
  updateProfile: (data: {
    displayName?: string;
    email?: string;
    phone?: string;
    baseAirport?: string;
  }) => api.put('/user/profile/', data),
  
  updateEflightCredentials: (data: {
    eflightUsername: string;
    eflightPassword: string;
  }) => api.put('/user/eflight-credentials/', data),
  
  updateSyncSettings: (data: {
    syncEnabled?: boolean;
    syncMonths?: number;
    syncHistoryMonths?: number;
  }) => api.put('/user/sync-settings/', data),
  
  regenerateIcsToken: () =>
    api.post('/user/regenerate-ics-token/'),

  getReminderSettings: () =>
    api.get('/user/reminder_settings/') as Promise<{ data: { success: boolean; data: { landingReminderEnabled: boolean; remindAfterLandingMinutes: number } } }>,

  updateReminderSettings: (data: {
    landingReminderEnabled?: boolean;
    remindAfterLandingMinutes?: number;
  }) => api.put('/user/reminder_settings/', data),

  getBarkConfig: () =>
    api.get('/user/bark_key/') as Promise<{ data: { success: boolean; data: { barkKeyMask: string; barkServerUrl: string } } }>,

  updateBarkConfig: (data: {
    barkKey?: string;
    barkServerUrl?: string;
  }) => api.post('/user/bark_key/', data) as Promise<{
    data: {
      success: boolean;
      message: string;
      data?: { barkKeyMask: string; barkServerUrl: string };
    };
  }>,

  testBarkConfig: () =>
    api.post('/user/bark_key/') as Promise<{ data: { success: boolean; message: string } }>,

  getLinkSettings: () =>
    api.get('/user/link_settings/') as Promise<{ data: { success: boolean; data: LinkSettings } }>,

  updateLinkSettings: (data: Partial<LinkSettings>) =>
    api.put('/user/link_settings/', data) as Promise<{ data: { success: boolean; message: string } }>,

  /** 修改密码（需提供当前密码） */
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/user/changePassword/', data),
};

export interface FlightOperation {
  id: number;
  operationType: 'scheduled' | 'ferry' | 'training' | 'simulator' | 'other';
  operationStatus: 'planned' | 'landed_pending' | 'completed' | 'cancelled';
  logStatus: 'none' | 'user_confirmed' | 'fsop_imported' | 'reconciled' | 'conflict';
  enrichmentStatus: 'pending' | 'enriched';
  flightDate: string | null;
  flightNo: string;
  departure: string;
  arrival: string;
  aircraftType: string;
  aircraftRegistration: string;
  crewRole: string;
  plannedDepartureAt: string | null;
  plannedArrivalAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isManual: boolean;
}

export interface ManualFlightOperationCreateRequest {
  operationType?: FlightOperation['operationType'];
  flightDate: string;
  flightNo?: string;
  departure?: string;
  arrival?: string;
  aircraftType?: string;
  aircraftRegistration?: string;
  crewRole?: string;
  plannedDepartureAt?: string | null;
  plannedArrivalAt?: string | null;
}

export interface FlightSourceLinkReview {
  id: number;
  status: 'suggested' | 'confirmed' | 'rejected';
  matchMethod: string;
  matchScore: number;
  matchDetails: Record<string, unknown>;
  confirmedAt: string | null;
  source: {
    id: number;
    type: 'eflight' | 'fsop';
    externalId: string;
    capturedAt: string;
  };
  operation: {
    id: number;
    flightDate: string | null;
    flightNo: string;
    departure: string;
    arrival: string;
    operationStatus: string;
    logStatus: string;
  };
}

// Flight API
export const flightApi = {
  getFlights: (params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => api.get('/flights/', { params }),
  
  getFlight: (id: number) => api.get(`/flights/${id}/`),
  
  getCalendar: (year: number, month: number) =>
    api.get('/flights/calendar/', { params: { year, month } }),
  
  getUpcoming: () => api.get('/flights/upcoming/'),

  getUpcomingLegs: () => api.get('/flights/upcoming-legs/'),

  getStats: () => api.get('/flights/stats/'),

  getMonthHours: () => api.get('/flights/month-hours/'),

  getRouteStats: (period: 'week' | 'month' | 'year', excludeBase?: boolean) =>
    api.get('/flights/route-stats/', { params: { period, excludeBase } }),

  autoLinkLegs: () =>
    api.post('/flights/legs/auto-link/') as Promise<{ data: { success: boolean; data: { taskId: string } } }>,

  getUnlinkedLegsCount: () =>
    api.get('/flights/legs/unlinked/') as Promise<{ data: { success: boolean; data: { count: number } } }>,

  getUnlinkedLegs: (params?: { page?: number; page_size?: number }) =>
    api.get('/flights/legs/unlinked/', { params }) as Promise<{ data: { success: boolean; data: { results: UnlinkedLeg[]; count: number; page: number; total_pages: number } } }>,

  getSkippedLegs: (params?: { page?: number; page_size?: number }) =>
    api.get('/flights/legs/unlinked/', { params: { ...params, show_skipped: 'true' } }) as Promise<{ data: { success: boolean; data: { results: UnlinkedLeg[]; count: number; page: number; total_pages: number } } }>,

  skipLeg: (id: number, reason?: string) =>
    api.post(`/flights/legs/${id}/skip/`, { reason: reason ?? '' }) as Promise<{ data: { success: boolean; message: string } }>,

  unskipLeg: (id: number) =>
    api.post(`/flights/legs/${id}/unskip/`) as Promise<{ data: { success: boolean; message: string } }>,

  getAutoLinkLogs: () =>
    api.get('/flights/legs/auto-link-logs/') as Promise<{ data: { success: boolean; data: AutoLinkLogEntry[] } }>,

  createManualOperation: (payload: ManualFlightOperationCreateRequest) =>
    api.post('/flights/operations/', payload) as Promise<{ data: { success: boolean; data: FlightOperation } }>,

  getSuggestedSourceLinks: () =>
    api.get('/flights/source-links/suggested/') as Promise<{ data: { success: boolean; data: FlightSourceLinkReview[] } }>,

  confirmSourceLink: (id: number) =>
    api.post(`/flights/source-links/${id}/confirm/`) as Promise<{ data: { success: boolean; data: FlightSourceLinkReview; message: string } }>,

  rejectSourceLink: (id: number) =>
    api.post(`/flights/source-links/${id}/reject/`) as Promise<{ data: { success: boolean; data: FlightSourceLinkReview; message: string } }>,
};

/** 机场 — 后端直接返回 camelCase 格式 */
export interface Airport {
  code: string;
  iata: string;
  icao: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
  source: string;
  createdAt: string;
  updatedAt: string;
}

// Airport API
export const airportApi = {
  get: (code: string) =>
    api.get(`/airports/${encodeURIComponent(code)}/`),

  search: (q: string) =>
    api.get('/airports/search/', { params: { q } }),

  upsertManual: (code: string, data: {
    lat: number;
    lng: number;
    name?: string;
    city?: string;
    country?: string;
  }) => api.put(`/airports/${encodeURIComponent(code)}/manual/`, data),
};

// Unlinked leg (from FlightLegListSerializer)
export interface UnlinkedLeg {
  id: number;
  flightNo: string;
  flightDate: string;
  depCode: string;
  arrCode: string;
  depShortName: string;
  arrShortName: string;
  planDepTime: string | null;
  postName: string;
  linkSkipped: boolean;
  linkSkipReason: string;
}

export interface AutoLinkLogEntry {
  id: number;
  status: 'running' | 'success' | 'partial' | 'failed';
  linkedCount: number;
  ambiguousCount: number;
  manualSkippedCount: number;
  errorMessage: string;
  triggeredAt: string;
  completedAt: string | null;
}

// Link Settings
export interface LinkSettings {  autoLinkAfterEflightSync: boolean;
  autoLinkAfterFsopSync: boolean;
  unlinkedReminderEnabled: boolean;
  unlinkedReminderDays: number;
  lastAutoLinkAt: string | null;
  lastAutoLinkLinked: number;
  lastAutoLinkSkipped: number;
}

// Sync API
export const syncApi = {
  getLogs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/sync/logs/', { params }) as Promise<{ data: PaginatedResult<import('@/types').SyncLog> }>,

  getLog: (id: number) => api.get(`/sync/logs/${id}/`),

  getLogBatchDetails: (id: number) =>
    api.get(`/sync/logs/${id}/batch_details/`) as Promise<{ data: SyncLogBatchDetailsResponse }>,

  trigger: (months?: number, historyMonths?: number) =>
    api.post('/sync/trigger/', { months, historyMonths }),

  getStatus: () => api.get('/sync/status/'),
};

// Admin API
export const adminApi = {
  getUsers: () => api.get('/admin/users/'),

  getUser: (id: number) => api.get(`/admin/users/${id}/`),

  updateUser: (id: number, data: object) =>
    api.put(`/admin/users/${id}/`, data),

  deleteUser: (id: number) => api.delete(`/admin/users/${id}/`),

  syncAll: () => api.post('/sync/all/'),

  getStats: () => api.get('/admin/stats/'),
};

// =============================================================================
// FSOP API
// =============================================================================

/** DRF 标准分页结果 */
export interface PaginatedResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** 凭证 — 后端直接返回 camelCase 格式 */
export interface FsopCredential {
  id: number;
  archiveNo: string;
  licenseNum: string;
  phone: string;
  // messid 完整值不再返回，仅返回掩码用于识别
  messidMask?: string;
  isActive: boolean;
  checkedAt: string | null;
  isValid: boolean | null;
  createdAt: string;
  updatedAt: string;
}

/** 视图配置 */
export interface ViewConfig {
  id: number;
  name: string;
  selectedFields: string[];
  sortBy: string;
  sortOrder: string;
  isDefault: boolean;
  pageSize: number;
  filters: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface ViewConfigPayload {
  name: string;
  selectedFields: string[];
  sortBy: string;
  sortOrder: string;
  isDefault: boolean;
  pageSize: number;
  filters: Record<string, string>;
}

/** 飞行记录 — 后端直接返回 camelCase 格式 */
export interface FlightLog {
  id: number;
  recordId: string;
  airDate: string;
  airNum: string;
  airModel: string;
  airline: string;
  departure: string;
  arrival: string;
  flightTime: number | null;
  nightTime: number | null;
  dayTime: number | null;
  recordType: string;
  state: string;
  operation?: {
    id: number;
    operationStatus: string | null;
    logStatus: string | null;
  } | null;
  notesCount?: number;
  [key: string]: any;
}

/** 筛选项枚举 — 后端直接返回 camelCase 格式 */
export interface FilterOptions {
  recordTypes: string[];
  airNums: string[];
  airModels: string[];
  airlines: string[];
}

/** 统计数据 — 后端直接返回 camelCase 格式 */
export interface Statistics {
  totalRecords: number;
  recordTypes: Record<string, number>;
  totalFlightHours: number;
  totalNightHours: number;
  recentRecords: number;
}

/** 同步任务 — 后端直接返回 camelCase 格式 */
export interface SyncTask {
  id: string;
  status: string;
  stage: string;
  message: string;
  errorMessage: string | null;
  isCancelled: boolean;
  currentPage: number;
  totalListRecords: number;
  newListRecords: number;
  skippedListRecords: number;
  totalDetailRecords: number;
  processedRecords: number;
  successCount: number;
  failedCount: number;
  progress: {
    overallPercentage: number;
    listPercentage: number;
    detailPercentage: number;
    list: { currentPage: number; totalRecords: number; newRecords: number; skippedRecords: number };
    detail: { totalRecords: number; processed: number; success: number; failed: number };
  };
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

/** 同步状态总览 — 后端直接返回 camelCase 格式 */
export interface SyncStatus {
  totalRecords: number;
  hasDetailCount: number;
  pendingDetailCount: number;
  isSyncing: boolean;
  hasCredential: boolean;
  lastSyncTask: SyncTask | null;
}

/** 下载日志 — 后端直接返回 camelCase 格式 */
export interface DownloadLog {
  id: number;
  level: string;
  stage: string;
  message: string;
  logId: string | null;
  createdAt: string;
  exceptionType: string | null;
  exceptionMessage: string | null;
}

export const fsopApi = {
  // ── 凭证 ───────────────────────────────────────────────────────────────
  getMyCredential: () =>
    api.get('/fsop/credentials/my/'),

  createCredential: (payload: { archiveNo: string; licenseNum: string; phone: string; messid?: string }) =>
    api.post('/fsop/credentials/', payload),

  updateCredential: (id: number, payload: { archiveNo: string; licenseNum: string; phone: string; messid?: string }) =>
    api.put(`/fsop/credentials/${id}/`, payload),

  deleteCredential: (id: number) =>
    api.delete(`/fsop/credentials/${id}/`),

  checkCredential: () =>
    api.post('/fsop/credentials/check/'),

  // ── 视图配置 ────────────────────────────────────────────────────────────
  getViewConfigs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/fsop/view-configs/', { 
      params: {
        page: params?.page,
        pageSize: params?.pageSize,  // camelCase
      }
    }) as any,

  createViewConfig: (payload: ViewConfigPayload) =>
    api.post('/fsop/view-configs/', payload),

  updateViewConfig: (id: number, payload: ViewConfigPayload) =>
    api.put(`/fsop/view-configs/${id}/`, payload),

  deleteViewConfig: (id: number) =>
    api.delete(`/fsop/view-configs/${id}/`),

  setDefaultViewConfig: (id: number) =>
    api.post(`/fsop/view-configs/${id}/set_default/`),

  // ── 飞行记录 ────────────────────────────────────────────────────────────
  getFlightLogs: (params?: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    order?: string;
    search?: string;
    recordType?: string;
    airNum?: string;
    airModel?: string;
    airline?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    if (!params) return api.get('/fsop/flight-logs/') as any;
    const { page, pageSize, sortBy, order, search, recordType, airNum, airModel, airline, startDate, endDate } = params;
    return api.get('/fsop/flight-logs/', {
      params: {
        page,
        pageSize,      // camelCase
        sortBy,        // camelCase
        order,
        search,
        recordType,    // camelCase
        airNum,        // camelCase
        airModel,      // camelCase
        airline,
        startDate,     // camelCase
        endDate,       // camelCase
      },
    }) as any;
  },

  getFlightLog: (id: number) =>
    api.get(`/fsop/flight-logs/${id}/`) as Promise<{ data: { success: boolean; data: FlightLog } }>,

  getFlightLogsWithConfig: (configId: number, params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    order?: string;
    search?: string;
    recordType?: string;
    airNum?: string;
    airModel?: string;
    airline?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const { page, pageSize, sortBy, order, search, recordType, airNum, airModel, airline, startDate, endDate } = params;
    return api.get('/fsop/flight-logs/with_config/', {
      params: {
        configId,      // camelCase
        page,
        pageSize,      // camelCase
        sortBy,        // camelCase
        order,
        search,
        recordType,    // camelCase
        airNum,        // camelCase
        airModel,      // camelCase
        airline,
        startDate,     // camelCase
        endDate,       // camelCase
      },
    }) as any;
  },

  getFilterOptions: () =>
    api.get('/fsop/flight-logs/filter_options/'),

  getStatistics: () =>
    api.get('/fsop/flight-logs/statistics/'),

  getRecentLogs: (limit = 10) =>
    api.get('/fsop/flight-logs/recent/', { params: { limit } }),

  // ── 航线地图 ────────────────────────────────────────────────────────────
  getRoutes: (params?: { year?: number; mergeDirection?: boolean }) =>
    api.get('/fsop/flight-logs/routes/', { params }),

  // ── 同步 ────────────────────────────────────────────────────────────────
  getSyncStatus: () =>
    api.get('/fsop/sync/status/'),

  startSync: () =>
    api.post('/fsop/sync/start/', {}),

  cancelSync: (taskId: string) =>
    api.post('/fsop/sync/cancel/', { taskId }),

  getSyncTasks: (params?: { page?: number; pageSize?: number }) =>
    api.get('/fsop/sync/tasks/', { params }) as any,

  getSyncProgress: (taskId: string) => {
    if (!taskId || taskId === 'undefined') {
      return Promise.reject(new Error('Invalid taskId'));
    }
    return api.get(`/fsop/sync/${taskId}/progress/`);
  },

  getSyncLogs: () =>
    api.get('/fsop/sync/logs/'),

  // ── 起落有效期 ────────────────────────────────────────────────────────────
  getLandingCurrency: () =>
    api.get('/fsop/flight-logs/landing_currency/'),
};

export default api;

// =============================================================================
// Notes API
// =============================================================================

export const notesApi = {
  syncToVault: (noteId: number) =>
    api.post(`/notes/${noteId}/sync_to_vault/`) as Promise<{ data: { success: boolean; message: string; data: { noteId: number; title: string; vaultSyncStatus: string } } }>,
};

// =============================================================================
// Vault Sync API
// =============================================================================

// =============================================================================
// Vault Sync API
// =============================================================================

export const vaultApi = {
  getConfig: () =>
    api.get('/notes/vault-config/') as Promise<{ data: { success: boolean; data: import('@/types/notes.types').VaultConfig } }>,

  updateConfig: (data: {
    webdavUrl?: string;
    webdavUser?: string;
    webdavPass?: string;
    basePath?: string;
    enabled?: boolean;
  }) =>
    api.post('/notes/vault-config/', data) as Promise<{ data: { success: boolean; message: string; data: import('@/types/notes.types').VaultConfig } }>,

  validateConfig: (data: {
    webdavUrl: string;
    webdavUser: string;
    webdavPass: string;
  }) =>
    api.post('/notes/vault-config/validate/', data) as Promise<{ data: import('@/types/notes.types').VaultValidateResult }>,

  sync: () =>
    api.post('/notes/vault-config/sync/') as Promise<{ data: { success: boolean; message: string; data: { taskId: string } } }>,

  getSyncStatus: () =>
    api.get('/notes/vault_sync_status/') as Promise<{ data: { success: boolean; data: import('@/types/notes.types').VaultSyncStatus } }>,

  getSyncLogs: (params?: { page?: number; pageSize?: number }) =>
    api.get('/notes/vault-config/sync-logs/', { params }) as Promise<{ data: { count: number; next: string | null; previous: string | null; results: import('@/types/notes.types').VaultSyncLog[] } }>,
};
